import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Search, X, Save, ArrowLeft } from 'lucide-react';
import './Contract.css';

const url = process.env.REACT_APP_BACKEND_URL;

const EditContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  
  // Search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  
  // Dropdown open states
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
  
  const [data, setData] = useState({
    cust_id: '',
    client_id: '',
    job_id: '',
    section: '', // Add section field
    damiinname: '',
    damincontact: '',
    nokname: '',
    nokcontact: '',
    bankname: '',
    accountname: '',
    accountno: '',
    contract_start: '',
    contract_end: '',
    regdate: '',
    description: ''
  });

  // Section options
  const sectionOptions = [
    "Full Time",
    "Part Time",
    "Day",
    "Night",
    "Morning",
    "Afternoon"
  ];

  // Fetch dropdown data and contract data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, clientsRes, jobsRes, contractRes] = await Promise.all([
          axios.get(`${url}/api/customer/all`),
          axios.get(`${url}/api/client/all`),
          axios.get(`${url}/api/jobcategory/all`),
          axios.get(`${url}/api/contract/${id}`)
        ]);
        
        setCustomers(customersRes.data.data || []);
        setClients(clientsRes.data.data || []);
        setJobs(jobsRes.data.data || []);
        
        const contract = contractRes.data.data;
        
        // Set selected customer search text
        const selectedCustomer = customersRes.data.data.find(c => c._id === (contract.cust_id?._id || contract.cust_id));
        if (selectedCustomer) {
          setCustomerSearch(`${selectedCustomer.fullname} - ${selectedCustomer.phone}`);
        }
        
        // Set selected client search text
        const selectedClient = clientsRes.data.data.find(c => c._id === (contract.client_id?._id || contract.client_id));
        if (selectedClient) {
          setClientSearch(`${selectedClient.name} - ${selectedClient.location}`);
        }
        
        // Set selected job search text
        const selectedJob = jobsRes.data.data.find(j => j._id === (contract.job_id?._id || contract.job_id));
        if (selectedJob) {
          setJobSearch(selectedJob.category_name);
        }
        
        setData({
          cust_id: contract.cust_id?._id || contract.cust_id,
          client_id: contract.client_id?._id || contract.client_id,
          job_id: contract.job_id?._id || contract.job_id,
          section: contract.section || '', // Set section from contract
          damiinname: contract.damiinname || '',
          damincontact: contract.damincontact || '',
          nokname: contract.nokname || '',
          nokcontact: contract.nokcontact || '',
          bankname: contract.bankname || '',
          accountname: contract.accountname || '',
          accountno: contract.accountno || '',
          contract_start: contract.contract_start ? contract.contract_start.split('T')[0] : '',
          contract_end: contract.contract_end ? contract.contract_end.split('T')[0] : '',
          regdate: contract.regdate ? contract.regdate.split('T')[0] : '',
          description: contract.description || ''
        });
        
        if (contract.cid_letter_upload) {
          setCurrentFile(contract.cid_letter_upload);
        }
      } catch (error) {
        toast.error('Failed to load contract data');
        navigate('/ViewContract');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.searchable-dropdown')) {
        setCustomerDropdownOpen(false);
        setClientDropdownOpen(false);
        setJobDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Filter functions
  const filteredCustomers = customers.filter(customer =>
    customer.fullname?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.phone?.includes(customerSearch)
  );

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.location?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredJobs = jobs.filter(job =>
    job.category_name?.toLowerCase().includes(jobSearch.toLowerCase())
  );

  // Get selected item details
  const getSelectedCustomer = () => {
    return customers.find(c => c._id === data.cust_id);
  };

  const getSelectedClient = () => {
    return clients.find(c => c._id === data.client_id);
  };

  const getSelectedJob = () => {
    return jobs.find(j => j._id === data.job_id);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const onFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCurrentFile = () => {
    setCurrentFile(null);
  };

  const validateForm = () => {
    if (!data.cust_id) {
      toast.error('Please select a customer');
      return false;
    }
    if (!data.client_id) {
      toast.error('Please select a client');
      return false;
    }
    if (!data.job_id) {
      toast.error('Please select a job category');
      return false;
    }
    if (!data.section) {
      toast.error('Please select a job section');
      return false;
    }
    if (!data.contract_start) {
      toast.error('Contract start date is required');
      return false;
    }
    if (!data.contract_end) {
      toast.error('Contract end date is required');
      return false;
    }
    if (new Date(data.contract_start) > new Date(data.contract_end)) {
      toast.error('Contract start date cannot be after end date');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const formData = new FormData();
      
      Object.keys(data).forEach(key => {
        if (data[key]) {
          formData.append(key, data[key]);
        }
      });
      
      if (selectedFile) {
        formData.append('cid_letter_upload', selectedFile);
      }
      
      // If current file is removed and no new file is selected
      if (!currentFile && !selectedFile) {
        formData.append('cid_letter_upload', '');
      }

      const response = await axios.put(`${url}/api/contract/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success('Contract updated successfully!');
        navigate('/ViewContract');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update contract');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading contract...</div>;

  return (
    <div className="contract-container">
      <div className="header">
        <h2>Edit Contract</h2>
        <button onClick={() => navigate('/ViewContract')} className="add-button">
          <ArrowLeft size={16} /> Back to List
        </button>
      </div>

      <form className="contract-form" onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-row">
          {/* Customer Searchable Dropdown */}
          <div className="form-group">
            <label>Customer *</label>
            <div className="searchable-dropdown">
              <div 
                className="dropdown-input"
                onClick={() => setCustomerDropdownOpen(!customerDropdownOpen)}
              >
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search customer by name or phone..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setCustomerDropdownOpen(true);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomerDropdownOpen(true);
                  }}
                />
                {data.cust_id && (
                  <X 
                    size={16} 
                    className="clear-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setData({ ...data, cust_id: '' });
                      setCustomerSearch('');
                    }}
                  />
                )}
              </div>
              {customerDropdownOpen && (
                <div className="dropdown-list">
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map(customer => (
                      <div
                        key={customer._id}
                        className={`dropdown-item ${data.cust_id === customer._id ? 'selected' : ''}`}
                        onClick={() => {
                          setData({ ...data, cust_id: customer._id });
                          setCustomerSearch(`${customer.fullname} - ${customer.phone}`);
                          setCustomerDropdownOpen(false);
                        }}
                      >
                        <div className="item-name">{customer.fullname}</div>
                        <div className="item-details">{customer.phone} - {customer.email}</div>
                      </div>
                    ))
                  ) : (
                    <div className="no-results">No customers found</div>
                  )}
                </div>
              )}
            </div>
            {data.cust_id && getSelectedCustomer() && (
              <div className="selected-info">
                Selected: {getSelectedCustomer().fullname} - {getSelectedCustomer().phone}
              </div>
            )}
          </div>

          {/* Client Searchable Dropdown */}
          <div className="form-group">
            <label>Client *</label>
            <div className="searchable-dropdown">
              <div 
                className="dropdown-input"
                onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
              >
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search client by name or location..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setClientDropdownOpen(true);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setClientDropdownOpen(true);
                  }}
                />
                {data.client_id && (
                  <X 
                    size={16} 
                    className="clear-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setData({ ...data, client_id: '' });
                      setClientSearch('');
                    }}
                  />
                )}
              </div>
              {clientDropdownOpen && (
                <div className="dropdown-list">
                  {filteredClients.length > 0 ? (
                    filteredClients.map(client => (
                      <div
                        key={client._id}
                        className={`dropdown-item ${data.client_id === client._id ? 'selected' : ''}`}
                        onClick={() => {
                          setData({ ...data, client_id: client._id });
                          setClientSearch(`${client.name} - ${client.location}`);
                          setClientDropdownOpen(false);
                        }}
                      >
                        <div className="item-name">{client.name}</div>
                        <div className="item-details">{client.location} | Capacity: {client.no_of_capacity}</div>
                      </div>
                    ))
                  ) : (
                    <div className="no-results">No clients found</div>
                  )}
                </div>
              )}
            </div>
            {data.client_id && getSelectedClient() && (
              <div className="selected-info">
                Selected: {getSelectedClient().name} - {getSelectedClient().location}
              </div>
            )}
          </div>

          {/* Job Category Searchable Dropdown */}
          <div className="form-group">
            <label>Job Category *</label>
            <div className="searchable-dropdown">
              <div 
                className="dropdown-input"
                onClick={() => setJobDropdownOpen(!jobDropdownOpen)}
              >
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search job category..."
                  value={jobSearch}
                  onChange={(e) => {
                    setJobSearch(e.target.value);
                    setJobDropdownOpen(true);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setJobDropdownOpen(true);
                  }}
                />
                {data.job_id && (
                  <X 
                    size={16} 
                    className="clear-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setData({ ...data, job_id: '' });
                      setJobSearch('');
                    }}
                  />
                )}
              </div>
              {jobDropdownOpen && (
                <div className="dropdown-list">
                  {filteredJobs.length > 0 ? (
                    filteredJobs.map(job => (
                      <div
                        key={job._id}
                        className={`dropdown-item ${data.job_id === job._id ? 'selected' : ''}`}
                        onClick={() => {
                          setData({ ...data, job_id: job._id });
                          setJobSearch(job.category_name);
                          setJobDropdownOpen(false);
                        }}
                      >
                        <div className="item-name">{job.category_name}</div>
                        {job.description && <div className="item-details">{job.description}</div>}
                      </div>
                    ))
                  ) : (
                    <div className="no-results">No job categories found</div>
                  )}
                </div>
              )}
            </div>
            {data.job_id && getSelectedJob() && (
              <div className="selected-info">
                Selected: {getSelectedJob().category_name}
              </div>
            )}
          </div>
        </div>

        {/* Section Dropdown */}
        <div className="form-row">
          <div className="form-group">
            <label>Job Section *</label>
            <select
              name="section"
              value={data.section}
              onChange={handleChange}
              required
              className="section-select"
            >
              <option value="">Select Job Section</option>
              {sectionOptions.map(section => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Contract Start Date *</label>
            <input
              type="date"
              name="contract_start"
              value={data.contract_start}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Contract End Date *</label>
            <input
              type="date"
              name="contract_end"
              value={data.contract_end}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Registration Date *</label>
            <input
              type="date"
              name="regdate"
              value={data.regdate}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>CID/Letter Upload (Image only)</label>
            <input
              type="file"
              name="cid_letter_upload"
              onChange={onFileChange}
              accept="image/*"
            />
            {preview ? (
              <div className="file-info">
                <img src={preview} alt="Preview" style={{ maxWidth: '100px', maxHeight: '60px' }} />
                <button type="button" onClick={() => {
                  setSelectedFile(null);
                  setPreview(null);
                }}>Remove</button>
              </div>
            ) : currentFile ? (
              <div className="file-info">
                <a href={`${url}${currentFile}`} target="_blank" rel="noopener noreferrer">
                  View Current File
                </a>
                <button type="button" onClick={removeCurrentFile}>Remove</button>
              </div>
            ) : null}
          </div>
        </div>

        <h3>Guarantor Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Guarantor Name (Damiin)</label>
            <input
              type="text"
              name="damiinname"
              value={data.damiinname}
              onChange={handleChange}
              placeholder="Enter guarantor name"
            />
          </div>

          <div className="form-group">
            <label>Guarantor Contact</label>
            <input
              type="text"
              name="damincontact"
              value={data.damincontact}
              onChange={handleChange}
              placeholder="Enter guarantor contact"
            />
          </div>
        </div>

        <h3>Next of Kin Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Next of Kin Name</label>
            <input
              type="text"
              name="nokname"
              value={data.nokname}
              onChange={handleChange}
              placeholder="Enter next of kin name"
            />
          </div>

          <div className="form-group">
            <label>Next of Kin Contact</label>
            <input
              type="text"
              name="nokcontact"
              value={data.nokcontact}
              onChange={handleChange}
              placeholder="Enter next of kin contact"
            />
          </div>
        </div>

        <h3>Bank Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Bank Name</label>
            <input
              type="text"
              name="bankname"
              value={data.bankname}
              onChange={handleChange}
              placeholder="Enter bank name"
            />
          </div>

          <div className="form-group">
            <label>Account Name</label>
            <input
              type="text"
              name="accountname"
              value={data.accountname}
              onChange={handleChange}
              placeholder="Enter account name"
            />
          </div>

          <div className="form-group">
            <label>Account Number</label>
            <input
              type="text"
              name="accountno"
              value={data.accountno}
              onChange={handleChange}
              placeholder="Enter account number"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Description (Optional)</label>
          <textarea
            name="description"
            value={data.description}
            onChange={handleChange}
            placeholder="Enter description"
            rows="3"
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? "Updating..." : "Update Contract"}
          </button>
          <button
            type="button"
            onClick={() => navigate('/ViewContract')}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditContract;